import type { TFunction } from 'i18next'

import type {
  ActivationGroup,
  ApiActivationGroupResource,
  ApiUpdateActivationGroup,
  ApiVolumeRangeUnit,
  AssignResourceToActivationGroup,
} from '@/features/activationGroup/types'
import { ActivationProvider } from '@/features/activationGroup/types'
import type { MarketProgram } from '@/features/bidding/constants'
import {
  MFRR_MARKET_PROGRAMS,
  MONOLITH_MARKET_PROGRAMS,
  NORDICS_FCRD_STATIC_MARKET_PROGRAMS,
  SYMBALANCE_MARKET_PROGRAMS,
} from '@/features/bidding/constants'

export function getActivationGroupByUuid(activationGroups: ActivationGroup[] | null, uuid: string | undefined) {
  return activationGroups && uuid ? activationGroups.find((activationGroup) => activationGroup.uuid === uuid) : null
}

/**
 * Whether the per-resource activatable flag is respected for the given activation group; the UI hides the
 * flag where it is not, to avoid implying an effect it does not have.
 *
 * The flag only has an effect where portfolio-manager activation schedules drive activation. It does not for:
 * - market programs whose resource selection is handled by the monolith (FCRD_UP/DOWN, FCRN, FFR_UP);
 * - Nordics FCR-D static (NORDICS_FCRD_UP_STATIC/NORDICS_FCRD_DOWN_STATIC), which does not use the flag;
 * - Symbalance (SYMBALANCE_UP/SYMBALANCE_DOWN), which does not use the flag; and
 * - mFRR under central or external control (only Sympower local control respects it there).
 */
export function isActivatableSupported(group: {
  marketProgram: MarketProgram
  activationProvider?: string | null
}): boolean {
  if (
    MONOLITH_MARKET_PROGRAMS.includes(group.marketProgram) ||
    NORDICS_FCRD_STATIC_MARKET_PROGRAMS.includes(group.marketProgram) ||
    SYMBALANCE_MARKET_PROGRAMS.includes(group.marketProgram)
  ) {
    return false
  }

  if (!MFRR_MARKET_PROGRAMS.includes(group.marketProgram)) {
    return true
  }

  return group.activationProvider === ActivationProvider.SYMPOWER_LOCAL
}

/**
 * The translated label for an activation provider, with a "Not Set" fallback for a missing value.
 */
export function getActivationProviderLabel(
  provider: ActivationProvider | null | undefined,
  t: TFunction<'translation'>,
): string {
  return provider
    ? t(`activation_groups.activation_provider.${provider.toLowerCase() as Lowercase<ActivationProvider>}`)
    : t('common.not_set')
}

/**
 * Serializes the given activation group resource and its associated data into an ApiUpdateActivationGroup object.
 *
 * @param assignedResource - The resource to be assigned to the activation group.
 * @param activationGroup - The activation group to which the resource is being assigned.
 * @param activationGroupResources - The existing resources of the activation group.
 * @returns An ApiUpdateActivationGroup object containing the serialized data.
 */
export function serializeAssignResourceToActivationGroup(
  assignedResource: AssignResourceToActivationGroup,
  activationGroup: ActivationGroup,
  activationGroupResources: ApiActivationGroupResource[],
): ApiUpdateActivationGroup {
  const updatedResources = activationGroupResources.map((resource) =>
    resource.uuid === assignedResource.resourceUuid
      ? {
          ...resource,
          qualifiedCapacity: assignedResource.qualifiedCapacity,
          activationPriority: assignedResource.activationPriority,
          isActivatable: assignedResource.isActivatable,
        }
      : resource,
  )

  // Check if the resource was updated; if not, add it as a new one
  const resourceExists = updatedResources.some((resource) => resource.uuid === assignedResource.resourceUuid)
  if (!resourceExists) {
    updatedResources.push({
      uuid: assignedResource.resourceUuid,
      qualifiedCapacity: assignedResource.qualifiedCapacity,
      activationPriority: assignedResource.activationPriority,
      isActivatable: assignedResource.isActivatable,
    } as ApiActivationGroupResource) // Cast to ApiActivationGroupResource to avoid type errors
  }

  return {
    code: activationGroup.code,
    biddableVolumeRange: activationGroup.biddableVolumeRange
      ? {
          unit: activationGroup.biddableVolumeRange.unit.toUpperCase() as ApiVolumeRangeUnit,
          min: activationGroup.biddableVolumeRange.min,
          max: activationGroup.biddableVolumeRange.max,
        }
      : null,
    state: activationGroup.state ?? 'LIVE',
    resourceCategory: activationGroup.resourceCategory,
    resources: updatedResources,
    regulationObjectCode: activationGroup.regulationObjectCode ?? null,
    transmissionArea: activationGroup.transmissionArea ?? null,
    tradingStrategy: activationGroup.tradingStrategy ?? null,
    reportingCode: activationGroup.reportingCode ?? null,
    externalCode: activationGroup.externalCode ?? null,
    activationProvider: activationGroup.activationProvider ?? null,
  }
}
